import React, { useState, useCallback, useMemo, useEffect } from 'react';
import * as Taskbar from 'mintel-taskbar';
import { useIntercom } from 'react-use-intercom';
import { useOidcAccessToken } from '@axa-fr/react-oidc';

const sidebarDefaults = {
  product: 'app_shell',
  key: 'sidebar',
};

/**
 * Wrapper around `mintel-taskbar`'s `Sidebar` that adds a callback function, `configureSidebar`, for adding panels and changing props
 * of the sidebar from child elements.
 */
const SidebarCallback = ({
  portalJwt,
  hideIntercom,
  children,
}: {
  portalJwt?: string;
  hideIntercom: boolean;
  children: (configureSidebar: ConfigureSidebarFn) => React.ReactNode;
}): JSX.Element => {
  const [, updateState] = useState({});
  const [panels, setPanels] = useState<Panels>({});
  const [sidebarProps, setSidebarProps] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { accessToken: oauthAccessToken } = useOidcAccessToken();
  const { update } = useIntercom();

  useEffect(() => {
    update?.({ hideDefaultLauncher: sidebarOpen || hideIntercom });
    updateState({});
  }, [update, sidebarOpen, hideIntercom]);

  /**
   * Sets panels and props to show in the taskbar. Calling this will trigger the sidebar to be re-rendered so wrap it
   * in a `React.useEffect`. If this function is called again, previous panels and props are dropped. Calling this
   * with no args will hide the taskbar.
   *
   * @param newPanels? panels to show in the sidebar. Key is the name of the panel and value are the props for that panel.
   * @param newProps? props for the sidebar.
   * @return Function that resets the taskbar to it's defaults. Used with `React.useEffect` to reset the taskbar on cleanup.
   */
  const configureSidebar: ConfigureSidebarFn = useCallback(
    (newPanels = {}, newProps = {}) => {
      setPanels(newPanels);
      setSidebarProps(newProps);

      // Clear out panels on cleanup in case the visible route doesn't configure the sidebar.
      return () => {
        setPanels({});
        setSidebarProps({});
      };
    },
    [setPanels, setSidebarProps],
  );

  const childrenElement = useMemo(
    () => <>{children(configureSidebar)}</>,
    [children, configureSidebar],
  );

  return (
    <>
      {Object.keys(panels).length > 0 && (
        <Taskbar.Sidebar
          portalJwt={portalJwt}
          oAuthAccessToken={oauthAccessToken}
          openCallback={(panel?: string) => {
            setSidebarOpen(Boolean(panel));
          }}
          {...sidebarDefaults}
          {...sidebarProps}
        >
          {Object.entries(panels).map(([panel, panelProps]) => {
            if (panel === 'CustomPanel') {
              return Array.isArray(panelProps) ? (
                panelProps.map((customProps) => (
                  <Taskbar.CustomPanel
                    key={`${panel}-${customProps.title}`}
                    {...customProps}
                  />
                ))
              ) : (
                <Taskbar.CustomPanel key={panel} {...panelProps} />
              );
            }

            const Panel = Taskbar[panel];
            return <Panel key={panel} {...panelProps} />;
          })}
        </Taskbar.Sidebar>
      )}
      {childrenElement}
    </>
  );
};

export default SidebarCallback;
